import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  Alert,
  TextField,
} from '@mui/material';
import { styled, useTheme as useMuiTheme } from '@mui/material/styles';
import { useLocation, useNavigate } from 'react-router-dom';
import { createUserWithEmailAndPassword, signInWithEmailAndPassword } from 'firebase/auth';
import { doc, setDoc, Timestamp } from 'firebase/firestore';
import { auth, db } from '../../firebase/config';
import { useAuth, type UserProfile } from '../../contexts/AuthContext';
import { SubscriptionPlan, formatPrice } from '../../constants/Subscriptions';
import { TimeZones } from '../../constants/TimeZones';
import {
  createRegistrationCheckout,
  redeemEnterpriseRegistrationCode,
  syncEnterpriseStripeMetadataForCompany,
} from '../../services/stripeWorkerClient';
import {
  getPriceIdForPlan,
  getRegistrationCheckoutUrls,
  planCategoryToWorkerCategory,
  planIdToWorkerSku,
} from '../../constants/stripePrices';

// Interfaces for type safety
interface Step1Data {
  companyName: string;
  title: string;
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
}

interface Step2Data {
  streetAddress1: string;
  streetAddress2: string;
  city: string;
  state: string;
  country: string;
  zipCode: string;
  phoneNumber: string;
}

type CombinedData = Step1Data & Step2Data & {
  plan: SubscriptionPlan;
  /** Matches plan selection on step 3; used for Stripe price (month vs year). */
  isAnnual?: boolean;
};

// Styled Components
const SummaryContainer = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '100vh',
  padding: '20px',
  background: '#07090e',
  backgroundImage: 'radial-gradient(ellipse 80% 50% at 50% 0%, rgba(34, 211, 238, 0.08) 0%, transparent 50%)',
}));

const SummaryPaper = styled(Paper)(({ theme }) => ({
  padding: '40px',
  borderRadius: '24px',
  maxWidth: '800px',
  width: '100%',
  backgroundColor: theme.palette.background.paper,
}));

const SectionContainer = styled(Box)(({ theme }) => ({
  marginBottom: '24px',
  padding: '20px',
  borderRadius: '16px',
  backgroundColor: theme.palette.background.default,
  border: `1px solid ${theme.palette.divider}`,
}));

const ActionButton = styled(Button)(({ theme }) => ({
  width: '100%',
  height: '50px',
  fontSize: '16px',
  fontWeight: 'bold',
  marginBottom: '12px',
}));

const CompleteButton = styled(ActionButton)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.common.white,
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
  },
  '&:disabled': {
    backgroundColor: theme.palette.action.disabled,
    color: theme.palette.action.disabled,
  },
}));

const BackButton = styled(ActionButton)(({ theme }) => ({
  backgroundColor: theme.palette.action.selected,
  color: theme.palette.text.secondary,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const REG_DRAFT_KEY = 'skimbuster_reg_draft';
const REG_CREDS_KEY = 'skimbuster_reg_credentials';

const RegisterSummary: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const muiTheme = useMuiTheme();
  const { hydrateUserProfileAfterSignup } = useAuth();
  const completingRef = React.useRef(false);

  const registrationData = location.state as CombinedData;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [enterpriseCode, setEnterpriseCode] = useState('');

  React.useEffect(() => {
    if (registrationData) return;
    const draft = sessionStorage.getItem(REG_DRAFT_KEY);
    if (draft) {
      try {
        const parsed = JSON.parse(draft) as CombinedData;
        navigate('/register/summary', { replace: true, state: parsed });
        return;
      } catch {
        sessionStorage.removeItem(REG_DRAFT_KEY);
      }
    }
    navigate('/register');
  }, [registrationData, navigate]);

  const handleCompleteRegistration = async () => {
    if (!registrationData) return;
    if (completingRef.current) return;
    completingRef.current = true;

    setLoading(true);
    setError('');

    try {
      const {
        companyName,
        title,
        firstName,
        lastName,
        email,
        password,
        streetAddress1,
        streetAddress2,
        city,
        zipCode,
        state,
        country,
        phoneNumber,
        plan,
        isAnnual = false,
      } = registrationData;

      const planSku = planIdToWorkerSku(plan.id);
      const workerCategory = planCategoryToWorkerCategory(plan.category);
      const isEnterprise =
        plan.id === 'Corp-Enterprise' ||
        plan.id === 'LEO-Enterprise' ||
        (plan.price === 0 && plan.id.toLowerCase().includes('enterprise'));

      /** Paid plans: Stripe Checkout first (single subscription). Worker creates Firebase after payment. */
      if (!isEnterprise) {
        const priceId = getPriceIdForPlan(plan, isAnnual);
        if (!priceId) {
          setError('Billing is not configured for this plan. Please choose another plan or contact support.');
          return;
        }

        sessionStorage.setItem(REG_DRAFT_KEY, JSON.stringify(registrationData));
        sessionStorage.setItem(REG_CREDS_KEY, JSON.stringify({ email, password }));

        const { successUrl, cancelUrl } = getRegistrationCheckoutUrls();
        const { checkoutUrl } = await createRegistrationCheckout({
          email,
          password,
          companyName,
          title,
          firstName,
          lastName,
          streetAddress1,
          streetAddress2,
          city,
          state,
          country,
          zipCode,
          phoneNumber,
          plan,
          isAnnual,
          priceId,
          planSku,
          category: workerCategory,
          successUrl,
          cancelUrl,
        });
        window.location.assign(checkoutUrl);
        return;
      }

      /** Enterprise: validate worker-issued code first (bypasses Stripe Checkout). */
      const trimmedCode = enterpriseCode.trim();
      if (!trimmedCode) {
        setError('Enter the enterprise activation code provided by SkimBuster.');
        return;
      }
      const redeemed = await redeemEnterpriseRegistrationCode({
        enterpriseCode: trimmedCode,
        email,
        planId: plan.id,
        category: workerCategory,
        companyName,
        firstName,
        lastName,
      });
      if (!redeemed.ok) {
        setError('Enterprise code was not accepted. Check the code and try again.');
        return;
      }

      /** Enterprise: create Firebase account after successful code redemption. */
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      const companyData = {
        companyName,
        title,
        firstName,
        lastName,
        email,
        streetAddress1,
        streetAddress2,
        city,
        zipCode,
        state,
        country,
        phoneNumber,
        subscriptionPlan: {
          available: plan.isAvailable,
          details: plan.description,
          id: plan.id,
          sku: planIdToWorkerSku(plan.id),
          title: plan.title,
          category: plan.category === 'law-enforcement' ? 'law' : 'corporate',
          billing: isAnnual ? 'annual' : 'monthly',
        },
        isAdmin: true,
        createdAt: Timestamp.now(),
        companyRadius: 5,
        ...(redeemed.subscription ? { subscription: redeemed.subscription } : {}),
      };

      await setDoc(doc(db, 'companies', user.uid), companyData);

      const userData = {
        name: `${firstName} ${lastName}`,
        email,
        role: 'Admin' as const,
        assignedSites: [],
        masterOwnerID: user.uid,
        ownerID: user.uid,
        announcements: true,
        lastLogin: Timestamp.now(),
        createdAt: Timestamp.now(),
        notificationTimeZone: TimeZones[0].value,
      };

      await setDoc(doc(db, 'users', user.uid), userData);

      const profileForAuth: UserProfile = {
        ...userData,
        subscriptionPlan: companyData.subscriptionPlan,
      };
      /** Immediately set React + localStorage so `onAuthStateChanged` sees a profile even if Firestore read lags rules/replication. */
      hydrateUserProfileAfterSignup(profileForAuth);

      const stripeCustomerId = redeemed.subscription?.stripeCustomerId;
      if (stripeCustomerId) {
        try {
          await syncEnterpriseStripeMetadataForCompany({
            stripeCustomerId,
            companyId: user.uid,
            userEmail: email,
            stripeSubscriptionId: redeemed.subscription?.stripeSubscriptionId || undefined,
            enterpriseCode: trimmedCode,
          });
        } catch (syncErr) {
          console.warn('Enterprise Stripe metadata sync skipped or failed:', syncErr);
        }
      }

      await signInWithEmailAndPassword(auth, email, password);
      sessionStorage.removeItem(REG_DRAFT_KEY);
      sessionStorage.removeItem(REG_CREDS_KEY);

      setSuccess(true);
      setTimeout(() => navigate('/profile'), 500);
    } catch (error: unknown) {
      console.error('Registration error:', error);
      const message = error instanceof Error ? error.message : 'An error occurred during registration. Please try again.';
      setError(message);
    } finally {
      completingRef.current = false;
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/register/step3', { state: registrationData });
  };

  if (!registrationData) {
    return null;
  }

  const {
    companyName,
    title,
    firstName,
    lastName,
    email,
    streetAddress1,
    streetAddress2,
    city,
    zipCode,
    state,
    country,
    phoneNumber,
    plan,
  } = registrationData;
  const isAnnual = registrationData.isAnnual ?? false;
  const isEnterprisePlan =
    plan.id === 'Corp-Enterprise' ||
    plan.id === 'LEO-Enterprise' ||
    (plan.price === 0 && plan.id.toLowerCase().includes('enterprise'));
  const expectsStripeCheckout = !isEnterprisePlan && !!getPriceIdForPlan(plan, isAnnual);

  return (
    <SummaryContainer>
      <SummaryPaper>
        <Typography
          variant="h4"
          component="h1"
          gutterBottom
          sx={{
            textAlign: 'center',
            fontWeight: 'bold',
            color: muiTheme.palette.primary.main,
            marginBottom: '32px',
          }}
        >
          Registration Summary
        </Typography>

        <Typography
          variant="body1"
          sx={{
            textAlign: 'center',
            color: muiTheme.palette.text.secondary,
            marginBottom: '32px',
          }}
        >
          Please review your information before completing registration
        </Typography>

        {/* Company Details Section */}
        <SectionContainer>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 'bold',
              color: muiTheme.palette.primary.main,
              marginBottom: '16px',
            }}
          >
            Company Details
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <Box>
              <Typography variant="body2" color="text.secondary">Company Name</Typography>
              <Typography variant="body1" fontWeight="500">{companyName}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">Title</Typography>
              <Typography variant="body1" fontWeight="500">{title}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">First Name</Typography>
              <Typography variant="body1" fontWeight="500">{firstName}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">Last Name</Typography>
              <Typography variant="body1" fontWeight="500">{lastName}</Typography>
            </Box>
            <Box sx={{ gridColumn: '1 / -1' }}>
              <Typography variant="body2" color="text.secondary">Email</Typography>
              <Typography variant="body1" fontWeight="500">{email}</Typography>
            </Box>
          </Box>
        </SectionContainer>

        {/* Address Details Section */}
        <SectionContainer>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 'bold',
              color: muiTheme.palette.primary.main,
              marginBottom: '16px',
            }}
          >
            Address Details
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <Box sx={{ gridColumn: '1 / -1' }}>
              <Typography variant="body2" color="text.secondary">Street Address</Typography>
              <Typography variant="body1" fontWeight="500">
                {streetAddress1}
                {streetAddress2 && `, ${streetAddress2}`}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">City</Typography>
              <Typography variant="body1" fontWeight="500">{city}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">State</Typography>
              <Typography variant="body1" fontWeight="500">{state}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">Country</Typography>
              <Typography variant="body1" fontWeight="500">{country}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">Zip Code</Typography>
              <Typography variant="body1" fontWeight="500">{zipCode}</Typography>
            </Box>
            <Box sx={{ gridColumn: '1 / -1' }}>
              <Typography variant="body2" color="text.secondary">Phone Number</Typography>
              <Typography variant="body1" fontWeight="500">{phoneNumber}</Typography>
            </Box>
          </Box>
        </SectionContainer>

        {/* Plan Selected Section */}
        <SectionContainer>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 'bold',
              color: muiTheme.palette.primary.main,
              marginBottom: '16px',
            }}
          >
            Plan Selected
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Subscription plan
              </Typography>
              <Typography variant="body1" fontWeight="600">
                {plan.title}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {plan.description}
              </Typography>
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: '12px' }}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Billing
                </Typography>
                <Typography variant="body1" fontWeight="500">
                  {isAnnual ? 'Annual' : 'Monthly'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Price
                </Typography>
                <Typography variant="body1" fontWeight="500">
                  {formatPrice(plan, isAnnual)}
                </Typography>
              </Box>
              <Box sx={{ gridColumn: { xs: '1 / -1', sm: '1 / -1' } }}>
                <Typography variant="body2" color="text.secondary">
                  Account type
                </Typography>
                <Typography variant="body1" fontWeight="500">
                  {plan.category === 'law-enforcement' ? 'Government' : 'Corporate'}
                </Typography>
              </Box>
            </Box>
            {(plan.maxSites != null ||
              plan.maxUsers != null ||
              plan.maxReports != null ||
              plan.historyRetention ||
              plan.hasGeoLocation) && (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>
                  Plan limits
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: '8px' }}>
                  {plan.maxSites != null && plan.maxSites > 0 && (
                    <Typography variant="body2" color="text.secondary">
                      Sites: up to <strong>{plan.maxSites}</strong>
                    </Typography>
                  )}
                  {plan.maxSites === 0 && plan.category === 'law-enforcement' && (
                    <Typography variant="body2" color="text.secondary">
                      Sites: <strong>Not included</strong> (field scans)
                    </Typography>
                  )}
                  {plan.maxUsers != null && (
                    <Typography variant="body2" color="text.secondary">
                      Users: up to <strong>{plan.maxUsers}</strong>
                    </Typography>
                  )}
                  {plan.maxReports != null && (
                    <Typography variant="body2" color="text.secondary">
                      Reports (approx.): <strong>{plan.maxReports.toLocaleString()}</strong>
                    </Typography>
                  )}
                  {plan.historyRetention && (
                    <Typography variant="body2" color="text.secondary">
                      History: <strong>{plan.historyRetention}</strong>
                    </Typography>
                  )}
                  {plan.hasGeoLocation && (
                    <Typography variant="body2" color="text.secondary">
                      Geo-location on scans: <strong>Yes</strong>
                    </Typography>
                  )}
                </Box>
              </Box>
            )}
            {plan.features?.length > 0 && (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>
                  Included with your plan
                </Typography>
                {isEnterprisePlan && (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>
                    Enterprise is tailored to you: limits are custom (not open-ended). All SkimBuster options are
                    available based on your contract.
                  </Typography>
                )}
                <Box component="ul" sx={{ m: 0, pl: 2.25, color: 'text.secondary' }}>
                  {plan.features.map((feature) => (
                    <Typography key={feature} component="li" variant="body2" sx={{ mb: 0.35 }}>
                      {feature}
                    </Typography>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        </SectionContainer>

        {isEnterprisePlan && (
          <SectionContainer>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 'bold',
                color: muiTheme.palette.primary.main,
                marginBottom: '12px',
              }}
            >
              Enterprise activation
            </Typography>
            <TextField
              fullWidth
              label="Enterprise activation code"
              placeholder="Enter your enterprise activation code"
              value={enterpriseCode}
              onChange={(e) => setEnterpriseCode(e.target.value)}
              disabled={loading || success}
              autoComplete="off"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Enterprise signup uses an activation code from SkimBuster. No payment step is required in the browser.
            </Typography>
          </SectionContainer>
        )}

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ marginBottom: '20px' }}>
            {error}
          </Alert>
        )}

        {/* Success Alert */}
        {success && (
          <Alert severity="success" sx={{ marginBottom: '20px' }}>
            Registration completed successfully! Redirecting to your dashboard...
          </Alert>
        )}

        {/* Action Buttons */}
        <Box sx={{ marginTop: '32px' }}>
          <CompleteButton
            onClick={handleCompleteRegistration}
            disabled={loading || success}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {loading
              ? expectsStripeCheckout
                ? 'Opening secure payment…'
                : 'Creating account…'
              : expectsStripeCheckout
                ? 'Continue to secure payment'
                : 'Complete registration'}
          </CompleteButton>
          
          <BackButton
            onClick={handleBack}
            disabled={loading || success}
          >
            Back to Plan Selection
          </BackButton>
        </Box>
      </SummaryPaper>
    </SummaryContainer>
  );
};

export default RegisterSummary;